import { Skeleton, toast } from "@credit-acceptance/uikit";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { usePullWorknumber } from "../api/usePullWorknumber";
import { useWorknumberEligibility } from "../api/useWorknumberEligibility";
import { useWorknumberHistory } from "../api/useWorknumberHistory";
import { W2_INCOME_TYPE_CODE } from "../constants";
import { PurchasedEmployerInfoPanel } from "./PurchasedEmployerInfoPanel";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface PullPurchasedEmployerInfoProperties {
  /** Deal / approval identifier — path param for all three worknumber calls. */
  approvalId?: string;
  /** Applicant identifier — path param for all three worknumber calls. */
  applicantId?: string;
  /** Stable id for the results table (aria + testing). */
  panelId: string;
  /** This income block's Income Type code — the feature only applies to W2 (OR-23450). */
  employmentType?: string;
  /** Called after every successful pull with whether its paired stips recalc was deferred (OR-23544). */
  onRecalcDeferred?: (deferred: boolean) => void;
  /**
   * Called once eligibility resolves, with whether recap-service's
   * `lastPullStatus` is `"NOTFOUND"` — i.e. no purchased employer data has
   * ever been found for this applicant. Callers surface this above the
   * Income section rather than inline here.
   */
  onEligibilityLastPullNotFound?: (notFound: boolean) => void;
  /** Applicant-level "no purchased employer data ever found" signal (OR/SVRS-56136) — disables the pull button so an analyst can't retry a pull recap-service has already reported as not found. */
  employerInfoNotFound?: boolean;
}

// ---------------------------------------------------------------------------
// PullPurchasedEmployerInfo
//
// Orchestrates the "Pull Purchased Employer Info" flow for one applicant income
// block:
//   1. /eligibility runs first. `canPull === true` → offer the pull button.
//   2. Button click → POST /worknumber pulls fresh history → render the table.
//   3. `canPull === false` → read /history; render the table if prior data exists.
//
// The whole feature stays inert until both ids are present AND the income
// block's Income Type is Employee (Gets W2) — approvalId, applicantId, and
// employmentType are all threaded from the Signers form, so it never fires a
// doomed request for an income type the feature doesn't apply to.
//
// Every endpoint failure (eligibility, history, pull) is surfaced as a toast
// rather than inline text.
// ---------------------------------------------------------------------------
export function PullPurchasedEmployerInfo({
  approvalId,
  applicantId,
  panelId,
  employmentType,
  onRecalcDeferred,
  onEligibilityLastPullNotFound,
  employerInfoNotFound,
}: PullPurchasedEmployerInfoProperties) {
  const { t } = useTranslation();
  // Feature only applies to Employee (Gets W2) income (OR-23450) — non-W2 income
  // types never fire the eligibility/history/pull calls at all.
  const isW2 = employmentType === W2_INCOME_TYPE_CODE;
  const hasIds = Boolean(approvalId && applicantId) && isW2;

  const eligibility = useWorknumberEligibility(approvalId ?? "", applicantId ?? "", { enabled: hasIds });
  const canPull = eligibility.data?.canPull === true;
  const notEligible = eligibility.isSuccess && !canPull;

  // Bubble up recap-service's "no purchased employer data ever found" signal so
  // SignerColumn can render a single warning above the Income section — resets
  // to `false` while the feature is inert (no ids/non-W2) or eligibility hasn't
  // resolved yet, so a stale warning never lingers.
  useEffect(() => {
    onEligibilityLastPullNotFound?.(hasIds && eligibility.data?.lastPullStatus === "NOTFOUND");
  }, [hasIds, eligibility.data?.lastPullStatus, onEligibilityLastPullNotFound]);

  // History is the fallback read — only when the applicant can't pull fresh data.
  const history = useWorknumberHistory(approvalId ?? "", applicantId ?? "", { enabled: hasIds && notEligible });

  const pull = usePullWorknumber();

  // TanStack Query v5 dropped per-query `onError`, so query failures are
  // toasted from an effect. Each ref tracks the last-toasted error so a
  // re-render (rather than a new failure) doesn't re-fire the same toast.
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const toastedEligibilityErrorReference = useRef<unknown>(undefined);
  useEffect(() => {
    if (eligibility.isError && eligibility.error !== toastedEligibilityErrorReference.current) {
      toastedEligibilityErrorReference.current = eligibility.error;
      toast.error(t("review.income.employerHistory.error", { message: getErrorMessage(eligibility.error) }));
    } else if (!eligibility.isError) {
      toastedEligibilityErrorReference.current = undefined;
    }
  }, [eligibility.isError, eligibility.error, t]);

  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const toastedHistoryErrorReference = useRef<unknown>(undefined);
  useEffect(() => {
    if (history.isError && history.error !== toastedHistoryErrorReference.current) {
      toastedHistoryErrorReference.current = history.error;
      toast.error(t("review.income.employerHistory.error", { message: getErrorMessage(history.error) }));
    } else if (!history.isError) {
      toastedHistoryErrorReference.current = undefined;
    }
  }, [history.isError, history.error, t]);

  if (!hasIds) {
    return null;
  }

  if (eligibility.isLoading) {
    return (
      <Skeleton.Input active size="small" aria-label={t("review.income.employerHistory.loading")} className="w-40" />
    );
  }

  // Eligibility failures still hide the feature (no button to offer) — the
  // toast above tells the analyst why it disappeared.
  if (eligibility.isError) {
    return null;
  }

  // Eligible: offer the pull button, then render whatever it pulls.
  if (canPull) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex justify-end">
          <Button
            type="button"
            variant="link"
            size="xs"
            className="cursor-pointer px-0 underline"
            disabled={pull.isPending || employerInfoNotFound}
            onClick={() =>
              pull.mutate(
                { approvalId: approvalId ?? "", applicantId: applicantId ?? "" },
                {
                  onSuccess: (result) => {
                    onRecalcDeferred?.(result.recalcDeferred);
                    // recap-service can respond with a non-200 (e.g. 202 Accepted) and
                    // an empty employers array — surface why instead of silently
                    // rendering nothing. Only warn when there are no rows to show,
                    // so we don't contradict a populated results table.
                    if (result.httpStatus !== 200 && result.history.rows.length === 0) {
                      toast.warning(
                        result.dataStatus === "NOTFOUND"
                          ? t("review.income.employerHistory.notFound")
                          : t("review.income.employerHistory.unexpectedStatus")
                      );
                    }
                  },
                  onError: (error) => {
                    toast.error(t("review.income.employerHistory.error", { message: getErrorMessage(error) }));
                  },
                }
              )
            }
          >
            {pull.isPending ? t("review.income.employerHistory.pulling") : t("review.income.pullEmployerInfo")}
          </Button>
        </div>
        {pull.data && pull.data.history.rows.length > 0 && (
          <PurchasedEmployerInfoPanel panelId={panelId} rows={pull.data.history.rows} />
        )}
      </div>
    );
  }

  // Not eligible: surface previously-pulled history if any exists.
  if (history.isLoading) {
    return (
      <Skeleton.Input active size="small" aria-label={t("review.income.employerHistory.loading")} className="w-40" />
    );
  }

  if (history.data && history.data.rows.length > 0) {
    return <PurchasedEmployerInfoPanel panelId={panelId} rows={history.data.rows} />;
  }

  return null;
}
